import { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { useAccountSettings } from "../Context/AccSettingContext";
import { initAnalytics, identifyUser, isAnalyticsReady } from "./analytics";
import { disableGtm, initGtm, isGtmReady, isValidContainerId, pushToDataLayer, resolveGtmConfig, setGtmUser } from "./gtm";
import { setTrackingAccount, trackPageView, trackPortalLanding } from "./tracking";
import { getAuthProfile } from "../utils/validation";

/**
 * Initializes the portal's analytics per account. PostHog is gated by the EEVT setting and
 * configured by ETCF; Google Tag Manager is a partner integration, enabled per account under
 * Partners with its own container id. The two are independent — an account may enable either, both,
 * or neither. PostHog reuses AccSettingContext, so it costs no extra settings request.
 */
export const useAnalytics = () => {
    const { accountSettings, getAccountsettingValue, account, isAuth, getAnalyticsConfig, getGtmPartner } = useAccountSettings();
    const initStartedRef = useRef(false);
    const gtmStartedRef = useRef(false);
    const phIdentifiedRef = useRef(false);
    const gtmIdentifiedRef = useRef(false);
    const lastPageRef = useRef(null);
    const [ready, setReady] = useState(false);
    const [gtmReady, setGtmReady] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setTrackingAccount(account?.code);
    }, [account]);

    useEffect(() => {
        if (initStartedRef.current || !Array.isArray(accountSettings)) return;
        if (getAccountsettingValue(accountSettings, "EEVT") !== "1") return;

        initStartedRef.current = true;
        (async () => {
            try {
                const res = await getAnalyticsConfig();
                if (!res?.key) {
                    initStartedRef.current = false; // allow a later retry
                    return;
                }

                let config = {};
                try {
                    const raw = getAccountsettingValue(accountSettings, "ETCF");
                    if (raw) config = JSON.parse(raw);
                } catch (e) {
                    console.error("Invalid ETCF event-tracking config", e);
                }
                if (res.host && !config.api_host) config.api_host = res.host;

                initAnalytics(res.key, config);
                setReady(isAnalyticsReady());
            } catch (e) {
                console.error("Failed to load analytics config", e);
                initStartedRef.current = false;
            }
        })();
    }, [accountSettings, getAccountsettingValue, getAnalyticsConfig]);

    // Load the account's GTM container, then raise the "landed on the Client Portal" milestone.
    // Google Tag Manager is a partner integration: the account admin turns it on and supplies the
    // container id under Partners. The BFF serves the partner payload without requiring a session,
    // because the container has to load on public pages, and resolveGtmConfig reduces it here.
    // Waits for account?.code so the landing event carries the tenant — the context resolves it
    // from the same boot, but the effects race.
    useEffect(() => {
        if (gtmStartedRef.current || !account?.code) return;

        gtmStartedRef.current = true;
        (async () => {
            try {
                const partnerData = await getGtmPartner();
                const { enabled, containerId } = resolveGtmConfig(partnerData ?? null);

                // The partner is off for this account: stop holding milestones for a container that
                // will never arrive.
                if (!enabled) return disableGtm();

                if (!isValidContainerId(containerId)) {
                    console.error("Invalid Google Tag Manager container id", containerId);
                    return disableGtm();
                }

                initGtm(containerId, getAuthProfile.getProfileAuth() ? localStorage.getItem("memberId") : null);
                if (isGtmReady()) {
                    setGtmReady(true);
                    trackPortalLanding();
                }
            } catch (e) {
                console.error("Failed to load Google Tag Manager config", e);
                gtmStartedRef.current = false; // allow a later retry
            }
        })();
    }, [account, getGtmPartner]);

    // Report navigation once a destination is listening. Both readiness flags are dependencies:
    // each initializes asynchronously after mount, and without them the very first page view is
    // dropped because nothing re-runs this effect when a destination finally comes up.
    useEffect(() => {
        if (!ready && !gtmReady) return;

        // PostHog and GTM come up independently, so this effect can run more than once for the
        // same location; without this guard that second run double-counts the page view.
        const page = `${location.pathname}${location.search}`;
        if (lastPageRef.current === page) return;
        lastPageRef.current = page;

        trackPageView(location);
    }, [location, ready, gtmReady]);

    // Identify the logged-in user with analytics once initialization is ready.
    useEffect(() => {
        if (!isAuth) return;
        if (!ready && !gtmReady) return;

        const userId = localStorage.getItem("memberId");
        if (!userId) return;

        if (ready && !phIdentifiedRef.current) {
            identifyUser(userId);
            phIdentifiedRef.current = true;
        }

        if (!gtmIdentifiedRef.current) {
            pushToDataLayer({ event: "dc_user_identified", user_id: userId });
            setGtmUser(userId);
            gtmIdentifiedRef.current = true;
        }
    }, [ready, gtmReady, isAuth]);
};
